import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { IconArrowLeft } from '@tabler/icons-react';
import { useProjectTrash, useRestoreProject, usePermanentlyDeleteProject } from '../../entities/project';
import { usePermissions } from '../../shared/permissions';
import { useDateFormat } from '../../shared/DateFormatContext';
import PageSection from '../../components/layout/PageSection';
import PageShell from '../../components/layout/PageShell';
import AppButton from '../../components/ui/AppButton';
import AppTable from '../../components/ui/AppTable';

const ProjectTrash = () => {
  const { t } = useTranslation();
  const { canManage } = usePermissions();
  const { formatDate } = useDateFormat();
  const [purgeModal, setPurgeModal] = useState({ show: false, project: null });

  const { data: projects = [], isLoading, error } = useProjectTrash();
  const restoreProject = useRestoreProject();
  const permanentlyDeleteProject = usePermanentlyDeleteProject();

  const handleRestore = (project) => {
    restoreProject.mutate(project.id, {
      onSuccess: () => toast.success(t('projects.trash.restoreSuccess', { name: project.name })),
      onError: () => toast.error(t('projects.trash.errorRestoring')),
    });
  };

  const handlePurgeConfirm = () => {
    const project = purgeModal.project;
    permanentlyDeleteProject.mutate(project.id, {
      onSuccess: () => {
        setPurgeModal({ show: false, project: null });
        toast.success(t('projects.trash.purgeSuccess', { name: project.name }));
      },
      onError: () => toast.error(t('projects.trash.errorPurging')),
    });
  };

  const columns = [
    {
      key: 'name',
      label: t('projects.projectName'),
      render: (row) => <strong>{row.name}</strong>,
    },
    {
      key: 'customer',
      label: t('projects.customer'),
      render: (row) => row.customer_name || t('common.notSet'),
    },
    {
      key: 'deleted_at',
      label: t('projects.trash.deletedAt'),
      render: (row) => (row.deleted_at ? formatDate(row.deleted_at) : t('common.notSet')),
    },
    {
      key: 'actions',
      label: t('common.actions'),
      render: (row) => (
        <div className="btn-group btn-group-sm">
          <AppButton
            variant="outline-secondary"
            size="sm"
            icon={<i className="ti ti-rotate" aria-hidden="true" />}
            loading={restoreProject.isPending}
            onClick={() => handleRestore(row)}
          >
            {t('projects.trash.restore')}
          </AppButton>
          {canManage && (
            <AppButton
              variant="outline-danger"
              size="sm"
              icon={<i className="ti ti-trash-x" aria-hidden="true" />}
              onClick={() => setPurgeModal({ show: true, project: row })}
            >
              {t('projects.trash.purge')}
            </AppButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageShell
        pretitle={t('projects.overview')}
        title={t('projects.trash.title')}
        description={t('projects.trash.description')}
        actions={(
          <AppButton as={Link} to="/projects" variant="outline-secondary" icon={<IconArrowLeft size={16} />}>
            {t('projects.trash.backToProjects')}
          </AppButton>
        )}
      >
        <PageSection bodyClassName="p-0">
          {error ? (
            <Alert variant="danger" className="app-inline-alert m-3">{t('projects.errorFetching')}</Alert>
          ) : (
            <AppTable
              columns={columns}
              data={projects}
              rowKey="id"
              loading={isLoading}
              emptyTitle={t('projects.trash.emptyTitle')}
              emptyMessage={t('projects.trash.emptyMessage')}
            />
          )}
        </PageSection>
      </PageShell>

      <Modal show={purgeModal.show} onHide={() => setPurgeModal({ show: false, project: null })} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('projects.trash.purge')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('projects.trash.purgeConfirm', { name: purgeModal.project?.name })}
        </Modal.Body>
        <Modal.Footer>
          <AppButton variant="outline-secondary" onClick={() => setPurgeModal({ show: false, project: null })}>
            {t('common.cancel')}
          </AppButton>
          <AppButton variant="danger" onClick={handlePurgeConfirm} loading={permanentlyDeleteProject.isPending}>
            {t('projects.trash.purge')}
          </AppButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProjectTrash;
